import { useEffect, useState } from 'react';
import { Button, Chip, Typography } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { ApiRequest } from 'GlobalFunctions';

interface BatchSummary {
  batch_id: number;
  warehouse_name: string;
  status: string;
  total_orders: number;
  success_count: number;
  failed_count: number;
  created_at: string;
  created_by_name: string;
}

interface Props {
  onOpenDetail: () => void;
  refreshTrigger?: number;
  onBatchesLoaded?: (batches: BatchSummary[]) => void;
}

export default function BatchCard({ onOpenDetail, refreshTrigger, onBatchesLoaded }: Props) {
  const [batches, setBatches] = useState<BatchSummary[]>([]);

  useEffect(() => {
    loadBatches();
  }, [refreshTrigger]);

  const loadBatches = () => {
    ApiRequest({
      url: '/orders/batches',
      method: 'get',
      headers: { ltkn: localStorage.getItem('ltkn') },
      query: { status: 'partial' },
      setResponse: (response: any) => {
        if (response.status === 'success') {
          const b = response.batches || [];
          setBatches(b);
          if (onBatchesLoaded) onBatchesLoaded(b);
        }
      },
    });
  };

  if (!batches.length) return null;

  const totalFailed = batches.reduce((s, b) => s + b.failed_count, 0);
  const totalSuccess = batches.reduce((s, b) => s + b.success_count, 0);
  const batchCount = batches.length;

  return (
    <div
      onClick={onOpenDetail}
      style={{
        background: '#fff',
        borderRadius: 5,
        padding: '14px 20px',
        marginBottom: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        boxShadow: 'rgba(145, 158, 171, 0.2) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px',
        cursor: 'pointer',
        transition: 'background 0.15s',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = '#fafafa')}
      onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <WarningAmberIcon style={{ color: '#ff9800', fontSize: 22 }} />
        <div>
          <Typography variant="body2" style={{ fontWeight: 500, color: '#333' }}>
            Facturación en bloque pendiente{batchCount > 1 ? ` · ${batchCount} bloques` : ''}
          </Typography>
          <Typography variant="caption" style={{ color: '#999' }}>
            {totalFailed} pedido{totalFailed > 1 ? 's' : ''} con error
          </Typography>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <CheckCircleOutlineIcon style={{ fontSize: 16, color: '#4caf50' }} />
          <Typography variant="body2" style={{ color: '#4caf50' }}>{totalSuccess}</Typography>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <ErrorOutlineIcon style={{ fontSize: 16, color: '#f44336' }} />
          <Typography variant="body2" style={{ color: '#f44336' }}>{totalFailed}</Typography>
        </div>
        <Button
          size="small"
          variant="contained"
          startIcon={<ReplayIcon style={{ fontSize: 14 }} />}
          onClick={(e) => { e.stopPropagation(); onOpenDetail(); }}
          style={{
            textTransform: 'none',
            fontSize: 12,
            backgroundColor: '#ff9800',
            boxShadow: 'none',
            borderRadius: 4,
            padding: '4px 12px',
          }}
        >
          Reintentar
        </Button>
      </div>
    </div>
  );
}
